import { useAuth } from '@/lib/auth/auth-context'
import { useOrganization } from '@/lib/organization/organization-context'
import { useTeamMembers } from '@/lib/api/use-teams'
import { Roles, type Role } from './roles'

/**
 * Returns the current user's TeamUser.role on the team they're currently
 * acting on (group, dealership, or bodyshop — picked from organization
 * context's isGroupView/activeTeamKind).
 *
 * Returns `null` while members are loading, when the user has no membership
 * on the active team, or when no team is active. Treat `null` as "no
 * specific role" — `useCanAccess` short-circuits this to `false` so nav
 * gates default to deny.
 *
 * Per-team-membership lookup is the only correct way to do this: the same
 * user can be a Director on the group and a General Manager on each child
 * dealership, so the answer must depend on the active team.
 */
export function useCurrentRole(): Role | null {
  const { activeGroup, activeDealership, activeBodyShop, activeTeamKind, isGroupView } =
    useOrganization()

  let teamId: number | null = null
  if (isGroupView && activeGroup) {
    teamId = Number(activeGroup.id)
  } else if (activeTeamKind === 'dealership' && activeDealership) {
    teamId = Number(activeDealership.id)
  } else if (activeTeamKind === 'bodyshop' && activeBodyShop) {
    teamId = Number(activeBodyShop.id)
  }

  return useTeamRole(teamId)
}

/**
 * Returns the current user's TeamUser.role on a specific team. Used by
 * Settings surfaces where the team being edited may differ from whatever
 * is active in OrganizationContext (the user can pick a team in the
 * Settings sidebar without changing their operational scope).
 *
 * Returns `null` when teamId is null/undefined, the members fetch is in
 * flight, or the user isn't a member of that team.
 *
 * Parent-group inheritance: a Director on a DealershipGroup has implicit
 * General-Manager-level access to every child dealership and bodyshop in
 * their group. The BE returns those child teams from GET /teams (see
 * team.service.ts → getTeamsForUser) and grants reads via
 * @AllowParentGroupDirector, but does NOT auto-create a TeamUser row on
 * the child — so direct membership lookup misses. Mirroring that here
 * means the FE permission gates match the BE's authorization model.
 */
export function useTeamRole(teamId: number | null | undefined): Role | null {
  const { user } = useAuth()
  const { activeGroup, availableDealerships, availableBodyShops } = useOrganization()
  const { data: members } = useTeamMembers(teamId ?? null)
  // The group's own members list is consulted only as a fallback (no direct
  // TeamUser on the queried team). Always call the hook — `enabled` keeps
  // it idle until activeGroup exists.
  const groupId = activeGroup ? Number(activeGroup.id) : null
  const { data: groupMembers } = useTeamMembers(groupId)

  if (!user?.email || !members) return null

  // Direct membership wins.
  const direct = members.find(
    m => m.email.toLowerCase() === user.email.toLowerCase(),
  )
  if (direct) return direct.role as Role

  // Inherited path. Three guards:
  //   1. There has to be an active group to inherit from.
  //   2. We don't want to "inherit" anything onto the group itself — the
  //      direct-membership branch above already handled that.
  //   3. The team must be one the BE actually returned in /teams (so we're
  //      not granting access to something the user has no relationship
  //      with, just because they happen to be a Director somewhere).
  if (!activeGroup || groupId == null || teamId == null) return null
  if (groupId === teamId) return null
  if (!groupMembers) return null

  const idStr = String(teamId)
  const isAvailable =
    availableDealerships.some(d => d.id === idStr) ||
    availableBodyShops.some(b => b.id === idStr)
  if (!isAvailable) return null

  const inGroup = groupMembers.find(
    m => m.email.toLowerCase() === user.email.toLowerCase(),
  )
  if (inGroup?.role !== Roles.Director) return null

  return Roles.GeneralManager
}

/**
 * Returns true if the current user can access something gated by a list of
 * roles, OR if the user is a system super-admin (`user.admin === true`).
 *
 * Super-admin always passes — system admins should see everything regardless
 * of team membership. The role list applies to per-team checks: nav items,
 * action buttons, page-level guards.
 */
export function useCanAccess(allowedRoles: readonly Role[]): boolean {
  const { user } = useAuth()
  const role = useCurrentRole()
  if (user?.admin) return true
  return role !== null && allowedRoles.includes(role)
}
